import {
  BulkCreateResultsDto,
  ContactListDto,
  ContactResponseDto,
  CreateContactDto,
  CreateMultipleContactsDto,
  FindAllParamsDto,
  LinkContactMultiParamsDto,
  LinkContactParamsDto,
  MergeContactParamsDto,
  UnlinkContactMultiParamsDto,
  UnlinkContactParamsDto,
  UpdateContactDto,
} from '@onereach/types-contacts-api';
import { defineStore } from 'pinia';

import ContactsApi from '@/api/ContactsApiV2';

type Collections = Record<string, ContactListDto>;

type Contacts = Record<string, ContactResponseDto>;

type CountCache = Record<string, { total: number; timestamp: number }>;

const CACHE_TTL = 60000;

const useContactsStore = defineStore('contactsV2', {
  state: () => ({
    all: {
      items: [],
      total: 0,
    } as ContactListDto,
    collections: {} as Collections,
    contacts: {} as Contacts,
    countCache: {} as CountCache,
  }),

  getters: {
    getContactsByBookId: (state) => {
      return (id: string): ContactListDto => {
        return state.collections[id]
          ? state.collections[id]
          : {} as ContactListDto;
      };
    },

    getAllContacts: (state) => {
      return (): ContactListDto => {
        return state.all as ContactListDto;
      };
    },

    getContactById: (state) => (contactId: string): ContactResponseDto => state.contacts[contactId],
  },

  actions: {
    _getCountCacheKey(payload: FindAllParamsDto): string {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { from, size, ...paramsWithoutPagination } = payload;
      return JSON.stringify(paramsWithoutPagination);
    },

    async getContacts(payload: FindAllParamsDto): Promise<void> {
      const cacheKey = this._getCountCacheKey(payload);
      const cached = this.countCache[cacheKey];
      const now = Date.now();

      let totalPromise: Promise<number>;

      if (cached && (now - cached.timestamp) < CACHE_TTL) {
        totalPromise = Promise.resolve(cached.total);
      } else {
        totalPromise = ContactsApi.contactApi.countContacts(payload)
          .then(total => {
            this.countCache[cacheKey] = {
              total,
              timestamp: now,
            };
            return total;
          });
      }

      const [items, total] = await Promise.all([
        ContactsApi.contactApi.findContacts(payload),
        totalPromise,
      ]);

      items.forEach(contact => this.contacts[contact.id] = contact);
      if (payload.contact_book) {
        this.collections[payload.contact_book] = {
          items,
          total,
        };
      } else {
        this.all = {
          items,
          total,
        };
      }
    },

    _updateContact(contact: ContactResponseDto, contactBook?: string): void {
      this.contacts[contact.id] = contact;
      if (contactBook) {
        const collection = this.collections[contactBook];
        if (collection) {
          const index = collection.items.findIndex(ct => ct.id === contact.id);
          if (index >= 0) {
            collection.items[index] = contact;
          } else {
            collection.items.push(contact);
          }
        }
      }
    },

    async updateContact(id: string, data: UpdateContactDto): Promise<void> {
      const contact = await ContactsApi.contactApi.updateContact(id, data);
      this._updateContact(contact, data.contact_book);
    },

    async createContact(data: CreateContactDto): Promise<void> {
      const contact = await ContactsApi.contactApi.createContact(data);
      this._updateContact(contact, data.contact_book);

      this.countCache = {};
    },

    async bulkCreateContacts(data: CreateMultipleContactsDto): Promise<BulkCreateResultsDto> {
      return ContactsApi.bulkCreateApi.bulkCreateContacts(`ui_bulk_${Date.now()}`, data);
    },

    async deleteContacts(contactIds: string[], bookId?: string): Promise<void> {
      await ContactsApi.contactApi.deleteMulti({
        contact_book: bookId,
        ids: contactIds,
      });

      contactIds.forEach(id => delete this.contacts[id]);
      if (bookId && this.collections[bookId]) {
        this.collections[bookId].items = this.collections[bookId].items
          .filter(ct => !contactIds.includes(ct.id));
        this.collections[bookId].total -= contactIds.length;
      }

      this.countCache = {};
    },

    async deleteAllContacts(contactBookId: string): Promise<void> {
      await ContactsApi.contactApi.deleteMulti({
        contact_book: contactBookId,
        all: true,
      });

      this.collections[contactBookId].items = [];
      this.collections[contactBookId].total = 0;

      this.countCache = {};
    },

    async mergeContacts(toId: string, fromId: string, bookId: string | undefined): Promise<void> {
      await ContactsApi.contactApi.mergeContacts(toId, {
        contact_book: bookId,
        mergeContactId: fromId,
      } as MergeContactParamsDto);
    },

    async linkContact(id: string, data: LinkContactParamsDto): Promise<void> {
      await ContactsApi.contactApi.linkContact(id, data);
    },

    async linkContactsMulti(data: LinkContactMultiParamsDto): Promise<void> {
      await ContactsApi.contactApi.linkContactsMulti(data);
    },

    async unlinkContact(id: string, data: UnlinkContactParamsDto): Promise<void> {
      await ContactsApi.contactApi.unlinkContact(id, data);
    },

    async unlinkContactsMulti(data: UnlinkContactMultiParamsDto): Promise<void> {
      await ContactsApi.contactApi.unlinkContactsMulti(data);
    },
  },
});

export default useContactsStore;
